import { useQueryClient } from '@tanstack/react-query';
import { tenant } from '@tenant';
import { default as Select } from 'antd/es/select';
import { LockIcon } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';

import { useOpenNotification } from 'utils/antd/notification-toasts';
import { inputMoneyFormatter } from 'utils/formatters/inputMoneyFormatter';
import { virtualAccountNumberFormatter } from 'utils/formatters/virtualAccountNumberFormatter';
import { hasValue } from 'utils/hasValue';

import type { GetCryptoBalances200 } from 'api/customerApi';
import {
  getGetCryptoExchangesQueryKey,
  useGetCryptoEligibleVbas,
  usePostCryptoExchanges
} from 'api/customerApi';

import { useVirtualAccounts } from 'hooks/context';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { Spinner } from '@atoms/Spinner/Spinner';

import { AmountInput } from '@molecules/AmountInput';

import {
  CRYPTO_EXCHANGE_ACCOUNT_UNAVAILABLE_TEST_ID,
  CRYPTO_EXCHANGE_CURRENCY_TEST_ID,
  CRYPTO_EXCHANGE_DESTINATION_DETAILS_LABEL_TEST_ID,
  CRYPTO_EXCHANGE_DESTINATION_HINT_LABEL_TEST_ID,
  CRYPTO_EXCHANGE_DESTINATION_INPUT_TEST_ID,
  CRYPTO_EXCHANGE_DESTINATION_TEST_ID,
  CRYPTO_EXCHANGE_LIMITS_LABEL_TEST_ID,
  CRYPTO_EXCHANGE_RATE_LOADING_TEST_ID
} from './constants';
import { CryptoExchangeRateDisplay } from './CryptoExchangeRateDisplay';
import { ExchangeConfirmationModal } from './ExchangeConfirmationModal';
import { ExchangeRateRefreshIndicator } from './ExchangeRateRefreshIndicator';
import { ExchangeSuccessView } from './ExchangeSuccessView';
import {
  extractExchangeError,
  extractQuoteError
} from './extractExchangeError';
import { useCryptoExchangeRate } from './useCryptoExchangeRate';

import { ASSET_DECIMALS, toSmallestAmount } from '../config';
import {
  CRYPTO_SELECTOR_STALE_TIME,
  CurrencyLabel,
  CurrencyOption,
  useCryptoSelection
} from '../shared/useCryptoSelection';
import { useVirtualBankAccount } from '../shared/VirtualBankAccountProvider';

type Props = {
  balances?: GetCryptoBalances200;
};

export const ExchangeTab = ({ balances: sharedBalances }: Props) => {
  // ### HOOKS
  const destinationId = useId();
  const { findAccountByAccountNumber } = useVirtualAccounts();
  const { accounts, virtualAccountNumber: selectedAccountNumber } =
    useVirtualBankAccount();

  const {
    organisationId,
    balances,
    currencyOptions,
    isCurrencyOptionsLoading,
    selectedAsset,
    selectedAssetLimits,
    handleCurrencyChange
  } = useCryptoSelection({
    balances: sharedBalances,
    shouldFetchBalances: false
  });

  const { data: eligibleVbasData, isLoading: isLoadingEligibleAccounts } =
    useGetCryptoEligibleVbas(
      organisationId,
      {
        asset: selectedAsset ?? ''
      },
      {
        query: {
          enabled: hasValue(selectedAsset),
          staleTime: CRYPTO_SELECTOR_STALE_TIME,
          refetchOnWindowFocus: false
        }
      }
    );

  const eligibleAccountNumbers = useMemo(
    () => (selectedAsset ? (eligibleVbasData?.data?.vbas ?? []) : []),
    [selectedAsset, eligibleVbasData]
  );

  const openNotification = useOpenNotification();
  const queryClient = useQueryClient();

  const {
    mutate: createExchange,
    isPending: isCreatingExchange,
    reset: resetCreateExchange
  } = usePostCryptoExchanges({
    mutation: {
      onError: (error) => {
        openNotification({
          kind: 'error',
          message: extractExchangeError(error)
        });
      },
      onSuccess: (_data, { organisationId }) => {
        queryClient.invalidateQueries({
          queryKey: getGetCryptoExchangesQueryKey(organisationId)
        });

        if (!hasValue(rateData?.proceeds_after_fx_markup)) {
          console.error('rate data is empty');
          return;
        }
        setSuccessfulExchangeAmount(rateData?.proceeds_after_fx_markup);
        setIsSuccessView(true);
      }
    }
  });

  // ### STATE
  const [amount, setAmount] = useState('');
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isSuccessView, setIsSuccessView] = useState(false);
  const [successfulExchangeAmount, setSuccessfulExchangeAmount] = useState<
    string | undefined
  >(undefined);

  // ### MEMOS
  const selectedAccount = useMemo(
    () =>
      accounts.find(
        (account) => account.account_number === selectedAccountNumber
      ) ?? findAccountByAccountNumber(selectedAccountNumber),
    [accounts, findAccountByAccountNumber, selectedAccountNumber]
  );

  const quoteAmount = useMemo(
    () => toSmallestAmount(amount, selectedAsset),
    [amount, selectedAsset]
  );

  const balanceMax = useMemo(() => {
    if (!hasValue(selectedAsset)) return 0;

    const balance =
      balances?.data?.balances?.find((item) => item.asset === selectedAsset)
        ?.balance ?? 0;

    return balance / 10 ** (ASSET_DECIMALS[selectedAsset] ?? 0);
  }, [balances?.data?.balances, selectedAsset]);

  // ### OTHER
  const toCurrency = selectedAccount?.currency_code;

  const pair =
    hasValue(selectedAsset) && hasValue(toCurrency)
      ? `${selectedAsset}_${toCurrency}`
      : undefined;
  const isSelectedAccountEligible =
    hasValue(selectedAccountNumber) &&
    eligibleAccountNumbers.includes(selectedAccountNumber);

  const allFieldsFilled =
    hasValue(selectedAsset) && hasValue(selectedAccountNumber) && amount !== '';

  const {
    rateData,
    isRateLoading,
    isRateError,
    rateError,
    secondsUntilExpiry,
    validitySeconds,
    isRateExpired
  } = useCryptoExchangeRate({
    organisationId,
    pair,
    amount: quoteAmount,
    virtualAccountNumber: selectedAccountNumber,
    enabled:
      allFieldsFilled &&
      isSelectedAccountEligible &&
      hasValue(selectedAccountNumber)
  });

  const showQuote =
    hasValue(selectedAsset) &&
    hasValue(toCurrency) &&
    hasValue(rateData) &&
    !isRateExpired;

  const minTradeAmount = selectedAssetLimits?.minAmount;
  const maxTradeAmount = rateData?.max_amount ?? selectedAssetLimits?.maxAmount;

  const liquidityMax = hasValue(rateData?.max_amount)
    ? parseFloat(rateData.max_amount.replace(/,/g, ''))
    : Infinity;

  const isLiquidityLimited = liquidityMax <= balanceMax;
  const parsedAmount = amount === '' ? NaN : Number(amount);

  const isAmountAboveBalance =
    amount !== '' && !isNaN(parsedAmount) && parsedAmount > balanceMax;

  // Reset amount when asset or account changes (affects max balance).
  useEffect(() => {
    setAmount('');
  }, [selectedAsset, selectedAccountNumber]);

  useEffect(() => {
    setIsSuccessView(false);
    setSuccessfulExchangeAmount(undefined);
    setIsConfirmationOpen(false);
    resetCreateExchange();
  }, [selectedAccountNumber, resetCreateExchange]);

  useEffect(() => {
    resetCreateExchange();
  }, [rateData?.quote_uuid, resetCreateExchange]);

  const rateErrorMessage = isRateError
    ? extractQuoteError(rateError, maxTradeAmount, selectedAsset)
    : null;
  const showSelectedAccountUnavailable =
    hasValue(selectedAsset) &&
    hasValue(selectedAccountNumber) &&
    !isLoadingEligibleAccounts &&
    !isSelectedAccountEligible;

  const onConfirmExchange = () => {
    const isRateExpiredAtConfirmation =
      hasValue(rateData?.expiry_at) && rateData.expiry_at <= Date.now() / 1000;

    if (
      isRateExpired ||
      isRateExpiredAtConfirmation ||
      !hasValue(rateData?.quote_uuid) ||
      !hasValue(selectedAccountNumber)
    ) {
      return;
    }
    createExchange({
      organisationId,
      data: {
        quote_uuid: rateData.quote_uuid,
        virtual_account_number: selectedAccountNumber
      }
    });
    setIsConfirmationOpen(false);
  };

  const onNewExchange = () => {
    setIsSuccessView(false);
    setSuccessfulExchangeAmount(undefined);
    setAmount('');
    setIsConfirmationOpen(false);
    resetCreateExchange();
  };

  if (isSuccessView) {
    return (
      <ExchangeSuccessView
        successfulExchangeAmount={successfulExchangeAmount}
        toCurrency={toCurrency}
        onNewExchange={onNewExchange}
      />
    );
  }

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex flex-1 flex-col">
        <div
          data-testid={CRYPTO_EXCHANGE_CURRENCY_TEST_ID}
          className="flex h-20 flex-col gap-1"
        >
          <label className="text-xs font-semibold text-content-secondary">
            From
          </label>
          <Select
            placeholder="Select crypto currency"
            value={selectedAsset}
            optionRender={(x) => (
              <CurrencyOption
                asset={String(x.value)}
                ledgerBalances={balances?.data?.balances}
              />
            )}
            labelRender={(x) => (
              <CurrencyLabel
                asset={String(x.value)}
                ledgerBalances={balances?.data?.balances}
              />
            )}
            onChange={handleCurrencyChange}
            options={currencyOptions}
            loading={isCurrencyOptionsLoading}
            notFoundContent={
              isCurrencyOptionsLoading && (
                <div className="flex justify-center py-2">
                  <Spinner />
                </div>
              )
            }
            className="w-full"
          />
        </div>
        <div className="mb-4 flex min-h-20 flex-col gap-1">
          {selectedAsset && (
            <>
              <label className="text-xs font-semibold text-content-secondary">
                Amount
              </label>
              <AmountInput
                value={amount}
                currency={selectedAsset}
                onChange={setAmount}
                placeholder="Enter amount"
                aria-invalid={parsedAmount > balanceMax || isRateError}
                status={
                  parsedAmount > balanceMax || isRateError ? 'error' : undefined
                }
                suffix={
                  <span
                    style={{
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}
                    onClick={() => {
                      setAmount(
                        isLiquidityLimited && hasValue(rateData?.max_amount)
                          ? rateData.max_amount.replace(/,/g, '')
                          : String(balanceMax)
                      );
                    }}
                  >
                    MAX
                  </span>
                }
              />
              <div className="min-h-6">
                {!(parsedAmount > balanceMax) &&
                  !hasValue(rateErrorMessage) &&
                  (hasValue(minTradeAmount) || hasValue(maxTradeAmount)) && (
                    <span
                      data-testid={CRYPTO_EXCHANGE_LIMITS_LABEL_TEST_ID}
                      className="text-xs text-content-secondary"
                    >
                      {hasValue(minTradeAmount) && `Min ${minTradeAmount}`}
                      {hasValue(minTradeAmount) &&
                        hasValue(maxTradeAmount) &&
                        ' · '}
                      {hasValue(maxTradeAmount) && `Max ${maxTradeAmount}`}{' '}
                      {selectedAsset}
                    </span>
                  )}
                {parsedAmount > balanceMax && (
                  <>
                    <span className="text-xs text-red-600">
                      Not enough balance.{' '}
                    </span>
                    <span className="text-xs text-content-secondary">
                      Max: {inputMoneyFormatter(balanceMax.toString())}{' '}
                      {selectedAsset}{' '}
                    </span>
                  </>
                )}
                {parsedAmount <= balanceMax && hasValue(rateErrorMessage) && (
                  <span className="text-xs text-red-600">
                    {rateErrorMessage}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
        <div
          data-testid={CRYPTO_EXCHANGE_DESTINATION_TEST_ID}
          className="flex shrink-0 flex-col gap-1"
        >
          {showSelectedAccountUnavailable ? (
            <p
              data-testid={CRYPTO_EXCHANGE_ACCOUNT_UNAVAILABLE_TEST_ID}
              className="text-sm text-content-secondary"
            >
              The selected account cannot receive {selectedAsset} exchanges.
              Select another account or contact support at{' '}
              <a href={`mailto:${tenant.support.email}`}>
                {tenant.support.email}
              </a>
              .
            </p>
          ) : (
            <>
              <label
                htmlFor={destinationId}
                className="text-xs font-semibold text-content-secondary"
              >
                To
              </label>
              <div className="flex items-center gap-2 rounded-2xl border border-border-default bg-surface-muted px-3 py-2 focus-within:ring-2 focus-within:ring-border-brand">
                <div className="min-w-0 flex-1">
                  <input
                    id={destinationId}
                    data-testid={CRYPTO_EXCHANGE_DESTINATION_INPUT_TEST_ID}
                    readOnly
                    value={
                      selectedAccount?.name ||
                      selectedAccount?.currency_name ||
                      ''
                    }
                    placeholder="Select an account above"
                    aria-describedby={`${destinationId}-details ${destinationId}-hint`}
                    className="w-full min-w-0 border-0 bg-transparent p-0 text-sm text-content-primary outline-none"
                  />
                  <p
                    id={`${destinationId}-details`}
                    data-testid={
                      CRYPTO_EXCHANGE_DESTINATION_DETAILS_LABEL_TEST_ID
                    }
                    className="text-xs text-content-secondary"
                  >
                    {selectedAccount && (
                      <>
                        {virtualAccountNumberFormatter(
                          selectedAccount.account_number
                        )}
                        {' · '}
                        {selectedAccount.currency_code}
                      </>
                    )}
                  </p>
                </div>
                <LockIcon
                  aria-hidden="true"
                  className="size-4 shrink-0 text-content-secondary"
                />
              </div>
              <p
                id={`${destinationId}-hint`}
                data-testid={CRYPTO_EXCHANGE_DESTINATION_HINT_LABEL_TEST_ID}
                className="text-xs text-content-secondary"
              >
                Destination follows the account selected above.
              </p>
            </>
          )}
        </div>
      </div>
      <div className="flex h-20 flex-col gap-1">
        {showQuote ? (
          <CryptoExchangeRateDisplay
            rateData={rateData}
            asset={selectedAsset}
            toCurrency={toCurrency}
          />
        ) : (
          isRateLoading && (
            <div
              data-testid={CRYPTO_EXCHANGE_RATE_LOADING_TEST_ID}
              className="flex h-full items-center justify-center"
            >
              <Spinner />
            </div>
          )
        )}
      </div>
      <PrimaryButton
        className="mt-auto"
        disabled={
          !allFieldsFilled ||
          Number(amount) <= 0 ||
          isRateLoading ||
          isRateExpired ||
          isAmountAboveBalance ||
          isLoadingEligibleAccounts ||
          !isSelectedAccountEligible ||
          !hasValue(rateData?.quote_uuid) ||
          isCreatingExchange
        }
        onClick={() => setIsConfirmationOpen(true)}
      >
        <span className="inline-flex items-center gap-2">
          Exchange
          <ExchangeRateRefreshIndicator
            secondsUntilExpiry={secondsUntilExpiry}
            validitySeconds={validitySeconds}
          />
        </span>
      </PrimaryButton>
      {hasValue(toCurrency) && hasValue(selectedAsset) && (
        <ExchangeConfirmationModal
          isOpen={isConfirmationOpen}
          fromCurrency={selectedAsset}
          toCurrency={toCurrency}
          amount={amount}
          rateData={rateData}
          secondsUntilExpiry={secondsUntilExpiry}
          validitySeconds={validitySeconds}
          isConfirming={isCreatingExchange}
          isRateExpired={isRateExpired}
          onClose={() => setIsConfirmationOpen(false)}
          onConfirm={onConfirmExchange}
        />
      )}
    </div>
  );
};
